import { useMemo, useState } from "react";
import { CheckCircle, ArrowsLeftRight, Warning, Prohibit, ListDashes, FileArrowDown } from "@phosphor-icons/react";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  mapped: "in line", converted: "unit converted", unit_mismatch: "value unreadable",
  option_mismatch: "not an allowed option", duplicate: "duplicate of another row",
  unmapped: "no field in data model", blank: "blank in template",
};

/** Delta report between a client specification template and the pack data model.
 *  Every row is accepted explicitly by the engineer (human in the loop). */
export default function SpecDelta({ result, accepted, onToggle, onEdit, custom, onToggleCustom }) {
  const [tab, setTab] = useState("mapped");
  const rows = result.rows || [];
  const groups = useMemo(() => ({
    mapped: rows.filter((r) => r.status === "mapped"),
    converted: rows.filter((r) => r.status === "converted"),
    attention: rows.filter((r) => ["unit_mismatch", "option_mismatch", "duplicate"].includes(r.status)),
    unmapped: rows.filter((r) => r.status === "unmapped"),
    uncovered: result.uncovered || [],
  }), [rows, result.uncovered]);

  const tabs = [
    { key: "mapped", label: "In line with the data model", count: groups.mapped.length, icon: CheckCircle, color: "var(--c-green)" },
    { key: "converted", label: "Unit converted", count: groups.converted.length, icon: ArrowsLeftRight, color: "var(--c-accent)" },
    { key: "attention", label: "Needs a decision", count: groups.attention.length, icon: Warning, color: "var(--c-amber)" },
    { key: "unmapped", label: "No field in data model", count: groups.unmapped.length, icon: Prohibit, color: "var(--c-red)" },
    { key: "uncovered", label: "Not in the template", count: groups.uncovered.length, icon: ListDashes, color: "var(--c-muted)" },
  ];
  const s = result.summary || {};

  return (
    <div className="space-y-3" data-testid="spec-delta">
      <div className="flex flex-wrap items-center gap-4 rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2.5 font-mono text-[11px]">
        <span className="text-[var(--c-text)]">{result.template?.name}</span>
        <span className="text-[var(--c-green)]">Mapped: {(s.mapped || 0) + (s.converted || 0)}/{s.rows || 0}</span>
        <span className="text-[var(--c-red)]">No field: {s.unmapped || 0}</span>
        <span className="text-[var(--c-amber)]">Not in template: {s.uncovered || 0}</span>
        <span className="text-[var(--c-muted)]">Model coverage {s.coverage_pct ?? 0}%</span>
        <button data-testid="spec-delta-export"
          onClick={() => window.open(`${BASE}/api/spec-templates/delta.xlsx?job_id=${result.staging_token}`, "_blank")}
          className="ml-auto inline-flex items-center gap-1.5 text-[var(--c-accent)] hover:underline">
          <FileArrowDown size={12} /> Delta sheet (xlsx)
        </button>
      </div>

      <div className="rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] overflow-hidden">
        <div className="flex flex-wrap border-b border-[var(--c-border)]">
          {tabs.map((t) => (
            <button key={t.key} data-testid={`spec-delta-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3.5 py-2.5 text-xs transition-colors duration-150 border-b-2
                ${tab === t.key ? "border-[var(--c-accent)] text-[var(--c-text)]" : "border-transparent text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
              <t.icon size={13} style={{ color: t.color }} /> {t.label}
              <span className="font-mono text-[10px] text-[var(--c-muted)]">{t.count}</span>
            </button>
          ))}
        </div>

        <div className="max-h-[26rem] overflow-y-auto">
          {tab === "uncovered" ? (
            groups.uncovered.length === 0 ? (
              <Empty text="The template covers every field in the data model." />
            ) : (
              <div className="divide-y divide-[var(--c-border)]">
                {groups.uncovered.map((u) => (
                  <div key={u.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`spec-uncovered-${u.id}`}>
                    <span className="text-xs text-[var(--c-text)]">{u.label}</span>
                    <span className={`font-mono text-[10px] ${u.required ? "text-[var(--c-red)]" : "text-[var(--c-muted)]"}`}>
                      {u.unit || u.type}{u.required ? " · required" : ""}
                    </span>
                  </div>
                ))}
              </div>
            )
          ) : tab === "unmapped" ? (
            groups.unmapped.length === 0 ? (
              <Empty text="Every template row has a home in the data model." />
            ) : (
              <table className="w-full text-xs">
                <tbody className="divide-y divide-[var(--c-border)]">
                  {groups.unmapped.map((r) => (
                    <tr key={r.row} data-testid={`spec-delta-row-${r.row}`}>
                      <td className="px-4 py-2.5 align-top w-[46%]">
                        <div className="text-[var(--c-text)]">{r.label}</div>
                        <div className="text-[10px] text-[var(--c-muted)] mt-0.5">{r.section || r.source}</div>
                      </td>
                      <td className="px-2 py-2.5 align-top font-mono text-[11px] text-[var(--c-text)] w-[24%]">
                        {r.raw_value ?? "—"} <span className="text-[var(--c-muted)]">{r.raw_unit}</span>
                      </td>
                      <td className="px-4 py-2 align-top text-right w-[30%]">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <span className="text-[10px] text-[var(--c-muted)]">Add to data model</span>
                          <Switch data-testid={`spec-custom-${r.row}`} checked={!!custom[r.row]}
                            onCheckedChange={(on) => onToggleCustom(r.row, on)} />
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : groups[tab].length === 0 ? (
            <Empty text="Nothing in this group." />
          ) : (
            <table className="w-full text-xs">
              <tbody className="divide-y divide-[var(--c-border)]">
                {groups[tab].map((r) => (
                  <tr key={r.row} className="hover:bg-[var(--c-bg)]/60 transition-colors duration-150"
                    data-testid={`spec-delta-row-${r.row}`}>
                    <td className="px-4 py-2.5 align-top w-[30%]">
                      <div className="text-[var(--c-text)]">{r.label}</div>
                      <div className="text-[10px] text-[var(--c-muted)] mt-0.5 font-mono">
                        {r.raw_value ?? "—"} {r.raw_unit}
                        {r.from_saved_template && <span className="ml-1.5 text-[var(--c-green)]">saved mapping</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2.5 align-top w-[26%]">
                      <div className="text-[var(--c-text)]">{r.field_label || r.field_id}</div>
                      <div className="text-[10px] text-[var(--c-muted)] mt-0.5 font-mono">
                        {r.conversion || STATUS_LABEL[r.status]}
                      </div>
                      {r.note && <div className="text-[10px] text-[var(--c-amber)] mt-0.5 leading-relaxed">{r.note}</div>}
                    </td>
                    <td className="px-2 py-2 align-top w-[22%]">
                      <Input data-testid={`spec-delta-value-${r.row}`}
                        value={Array.isArray(r.value) ? r.value.join("; ") : String(r.value ?? "")}
                        onChange={(e) => onEdit(r.row, e.target.value)}
                        className="h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono text-xs" />
                    </td>
                    <td className="px-2 py-2.5 align-top font-mono text-[10px] text-[var(--c-muted)] w-[10%]">{r.field_unit}</td>
                    <td className="px-4 py-2 align-top text-right w-[12%]">
                      <Switch data-testid={`spec-accept-${r.field_id || r.row}`}
                        checked={!!accepted[r.field_id]} disabled={!r.field_id}
                        onCheckedChange={(on) => onToggle(r.field_id, on)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="text-[10px] text-[var(--c-muted)] leading-relaxed">
        Rows switched on are written into the project specification on create, and from there drive the
        Design Engine, Validation and every drawing. A <span className="text-[var(--c-text)]">Specification
        Compliance</span> domain is added to Validation that re-checks each designed value against the
        value this template states.
      </div>
    </div>
  );
}

const Empty = ({ text }) => <div className="px-4 py-6 text-xs text-[var(--c-muted)]">{text}</div>;
